import React, { useEffect } from "react";

import { TForm, TFormValues } from "./types";
import config from "./config";

import useLocalStorage from "../../util/useLocalStorage";
import { useAnalytics } from "../../util/useAnalytics";

interface Options {
  redirect: boolean;
}

export type TGuideStore = {
  currentStep: string;
  setCurrentStep: (stepName: string, options: Options) => React.Dispatch<React.SetStateAction<string>>;
  currentConfig: TForm;
  setCurrentConfig: React.Dispatch<React.SetStateAction<TForm>>;
  analytics: {
    formStarted: (name: string) => void;
    stepCompleted: (
      stepName: string,
      stepNumber: string,
      formValues: TFormValues | null
    ) => void;
    submitForm: () => void;
    setEligibility: (eligibility: "eligible" | "ineligible") => void;
  };
};

// @ts-ignore
export const GuideContext = React.createContext<TGuideStore>(null);

export const GuideContextProvider: React.FC<{}> = ({ children }) => {
  const [redirectTo, setRedirectTo] = useLocalStorage<string>("redirectTo", '');
  const [currentConfig, setCurrentConfig] = React.useState<TForm>(config);
  const [currentStep, _setCurrentStep] = useLocalStorage<string>(
    "currentFormStep",
    config.start
  );
  const analytics = useAnalytics();

  useEffect(()=>{
    if(!window.location.pathname.startsWith('/apply/')) {
      setRedirectTo('');
      setCurrentStep('', {redirect: false});
    }
  }, [])

  const toggleGatewayForm = (step: string, options: Options = { redirect: true }) => {
    const shouldShow = step && currentStep !== step;
    if(shouldShow) {
      setRedirectTo(window.location.pathname);
      setCurrentStep(step, options);
      analytics.formStarted(step);
    } else {
      setCurrentStep('', options);
    }
  }

  const setCurrentStep = (stepName: string, options: Options = { redirect: true }) => {
    const config = currentConfig.steps[stepName];
    _setCurrentStep(stepName);
    if(config && config.stepPath && options.redirect){
      window.history.pushState(null, "", config.stepPath);
    }
    if(!stepName && !config && options.redirect){
      if(!['/free-legal-advice/', '/legal-resources/', '/legal-resources-results/'].includes(redirectTo)) {
        window.location.href = redirectTo;
      }
      window.history.pushState(null, "", redirectTo);
    }
  }

  const store = {
    currentStep,
    setCurrentStep,
    currentConfig,
    setCurrentConfig,
    toggleGatewayForm,
    analytics,
  };

  return (
    <GuideContext.Provider value={store}>{children}</GuideContext.Provider>
  );
};

export default GuideContextProvider;
